import { useState, useEffect, useCallback } from 'react';
import { useCommunity } from '../../context/community';

const STORAGE_KEYS = {
  COMPLETED: 'vtb_onboarding_completed',
  STEP: 'vtb_onboarding_step',
  SKIPPED: 'vtb_onboarding_skipped',
  LAST_SHOWN: 'vtb_onboarding_last_shown',
  FIRST_LOGIN: 'vtb_first_login'
};

export const useVTBOnboarding = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const { community } = useCommunity();

  // Check if onboarding should be shown on mount
  useEffect(() => {
    // Only run for VTB community
    if (community?.domain !== 'vtb') {
      return;
    }
    
    const completed = localStorage.getItem(STORAGE_KEYS.COMPLETED) === 'true';
    const skipped = localStorage.getItem(STORAGE_KEYS.SKIPPED) === 'true';
    const isFirstLogin = localStorage.getItem(STORAGE_KEYS.FIRST_LOGIN) !== 'false';
    
    setHasCompletedOnboarding(completed);
    
    // Check if other onboardings are active to prevent conflicts
    const hasOtherOnboardings = document.querySelector('[data-tour-active]') || 
                               document.querySelector('.modal-content:not([data-vtb-onboarding])');
    
    // Auto-show onboarding for first-time users (but not if other onboardings are active)
    if (isFirstLogin && !completed && !skipped && !hasOtherOnboardings) {
      // Small delay to ensure page is loaded
      setTimeout(() => {
        // Double-check no other onboardings started during delay
        const stillNoOtherOnboardings = !document.querySelector('[data-tour-active]') && 
                                       !document.querySelector('.modal-content:not([data-vtb-onboarding])');
        
        if (stillNoOtherOnboardings) {
          setIsOpen(true);
          localStorage.setItem(STORAGE_KEYS.FIRST_LOGIN, 'false');
        }
      }, 2000); // Increased delay to let other onboardings load first
    }

    // Restore step if previously saved
    const savedStep = localStorage.getItem(STORAGE_KEYS.STEP);
    if (savedStep) {
      setCurrentStep(parseInt(savedStep, 10));
    }
  }, [community]);

  const saveProgress = useCallback((step) => {
    localStorage.setItem(STORAGE_KEYS.STEP, step.toString());
    localStorage.setItem(STORAGE_KEYS.LAST_SHOWN, new Date().toISOString());
  }, []);

  const startOnboarding = useCallback(() => {
    // Check if other onboardings are active before starting
    const hasOtherOnboardings = document.querySelector('[data-tour-active]') || 
                               document.querySelector('.modal-content:not([data-vtb-onboarding])');
    
    if (!hasOtherOnboardings) {
      setIsOpen(true);
      setCurrentStep(1);
      saveProgress(1);
    } else {
      // Wait for other onboardings to finish, then start
      const checkInterval = setInterval(() => {
        const stillHasOthers = document.querySelector('[data-tour-active]') || 
                              document.querySelector('.modal-content:not([data-vtb-onboarding])');
        if (!stillHasOthers) {
          clearInterval(checkInterval);
          setIsOpen(true);
          setCurrentStep(1);
          saveProgress(1);
        }
      }, 1000);
      
      // Clear interval after 10 seconds to prevent infinite waiting
      setTimeout(() => clearInterval(checkInterval), 10000);
    }
  }, [saveProgress]);

  const nextStep = useCallback(() => {
    const newStep = Math.min(currentStep + 1, 4);
    setCurrentStep(newStep);
    saveProgress(newStep);
  }, [currentStep, saveProgress]);

  const prevStep = useCallback(() => {
    const newStep = Math.max(currentStep - 1, 1);
    setCurrentStep(newStep);
    saveProgress(newStep);
  }, [currentStep, saveProgress]);

  const skipOnboarding = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.SKIPPED, 'true');
    localStorage.setItem(STORAGE_KEYS.LAST_SHOWN, new Date().toISOString());
    setIsOpen(false);
    setCurrentStep(1);
  }, []);

  const completeOnboarding = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.COMPLETED, 'true');
    localStorage.setItem(STORAGE_KEYS.LAST_SHOWN, new Date().toISOString());
    localStorage.removeItem(STORAGE_KEYS.STEP);
    setHasCompletedOnboarding(true);
    setIsOpen(false);
    
    // Navigate to first section after completion
    setTimeout(() => {
      const earningsLink = document.querySelector('a[href="/vtb/earnings"]');
      if (earningsLink) {
        earningsLink.click();
      }
    }, 500);
  }, []);

  const resetOnboarding = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.COMPLETED);
    localStorage.removeItem(STORAGE_KEYS.SKIPPED);
    localStorage.removeItem(STORAGE_KEYS.STEP);
    localStorage.removeItem(STORAGE_KEYS.FIRST_LOGIN);
    setHasCompletedOnboarding(false);
    setCurrentStep(1);
  }, []);

  return {
    isOpen,
    currentStep,
    hasCompletedOnboarding,
    startOnboarding,
    nextStep,
    prevStep,
    skipOnboarding,
    completeOnboarding,
    resetOnboarding,
    setIsOpen
  };
};